/**
 * Data model for a planar, single-degree-of-freedom linkage built entirely from
 * revolute (pin) joints and rigid bars.
 *
 * Joint index space (a single flat array):
 *   [0 .. G-1]        ground pivots (fixed, coordinates are design parameters)
 *   [G]               the driven joint = tip of the driver crank
 *   [G+1 .. G+F]      free joints, positions found by the position solver
 *
 * The driver crank (from `driver.base` to the driven joint) is implicit — the
 * driven joint sits at a known function of the input angle theta — so it never
 * appears in `bars`. Every entry of `bars` is a rigid distance constraint that
 * the solver enforces.
 */

export type Topology = "fourbar" | "stephenson3";

export interface MechanismSpec {
  topology: Topology;
  /** Fixed pivot coordinates. Design parameters. */
  grounds: Array<[number, number]>;
  driver: {
    /** Ground joint index the crank is anchored to. */
    base: number;
    /** Crank length. Design parameter. */
    length: number;
    /** Crank angle offset added to the input theta. Design parameter. */
    phase: number;
    /** Rotation sense of the input. Structural, not optimized. */
    dir: 1 | -1;
  };
  /** Initial-guess coordinates for each free joint (Newton seed only). */
  free: Array<{ guess: [number, number] }>;
  /** Rigid links: [jointIndexA, jointIndexB, restLength]. restLength is a param. */
  bars: Array<[number, number, number]>;
  /**
   * The traced point, rigidly fixed to bar `bar`. `(u, v)` are coordinates in
   * that bar's local frame: origin at the bar's first joint, +x toward its
   * second joint, +y 90 deg counter-clockwise. Both are design parameters.
   */
  coupler: { bar: number; u: number; v: number };
}

/** Number of ground joints. */
export const groundCount = (m: MechanismSpec): number => m.grounds.length;
/** Flat index of the driven (crank-tip) joint. */
export const drivenIndex = (m: MechanismSpec): number => m.grounds.length;
/** Number of free joints. */
export const freeCount = (m: MechanismSpec): number => m.free.length;
/** Total joint count. */
export const jointCount = (m: MechanismSpec): number =>
  m.grounds.length + 1 + m.free.length;

/**
 * Serialize the optimizable scalars into a flat vector. Layout:
 *   [ ground coords (2G) | crank length | crank phase | bar lengths (B) | u | v ]
 */
export function packParams(m: MechanismSpec): number[] {
  const p: number[] = [];
  for (const [x, y] of m.grounds) p.push(x, y);
  p.push(m.driver.length, m.driver.phase);
  for (const b of m.bars) p.push(b[2]);
  p.push(m.coupler.u, m.coupler.v);
  return p;
}

/** Inverse of {@link packParams}: rebuild a spec from `base` + a flat vector. */
export function unpackParams(base: MechanismSpec, p: number[]): MechanismSpec {
  const G = base.grounds.length;
  const B = base.bars.length;
  let k = 0;
  const grounds: Array<[number, number]> = [];
  for (let i = 0; i < G; i++) grounds.push([p[k++], p[k++]]);
  const length = p[k++];
  const phase = p[k++];
  const bars = base.bars.map(
    (b, i) => [b[0], b[1], p[k + i]] as [number, number, number],
  );
  k += B;
  const u = p[k++];
  const v = p[k++];
  return {
    ...base,
    grounds,
    driver: { ...base.driver, length, phase },
    bars,
    coupler: { ...base.coupler, u, v },
  };
}

/** Number of scalars in the packed parameter vector. */
export const paramCount = (m: MechanismSpec): number =>
  2 * m.grounds.length + 2 + m.bars.length + 2;

/** Names for each packed parameter (UI + debugging). */
export function paramNames(m: MechanismSpec): string[] {
  const names: string[] = [];
  for (let i = 0; i < m.grounds.length; i++) names.push(`ground${i}.x`, `ground${i}.y`);
  names.push("crank.length", "crank.phase");
  m.bars.forEach((_, i) => names.push(`bar${i}.length`));
  names.push("coupler.u", "coupler.v");
  return names;
}

export function cloneSpec(m: MechanismSpec): MechanismSpec {
  return {
    topology: m.topology,
    grounds: m.grounds.map((g) => [g[0], g[1]] as [number, number]),
    driver: { ...m.driver },
    free: m.free.map((f) => ({ guess: [f.guess[0], f.guess[1]] as [number, number] })),
    bars: m.bars.map((b) => [b[0], b[1], b[2]] as [number, number, number]),
    coupler: { ...m.coupler },
  };
}
